import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import type { DashboardTheme } from './types';
import { SettingsColorPicker, SettingsSlider, SettingsOpacitySlider } from './controls';

interface DashboardThemeDialogProps {
  open: boolean;
  initial?: DashboardTheme;
  onClose: () => void;
  onSave: (theme: DashboardTheme | undefined) => void;
}

const DashboardThemeDialog: React.FC<DashboardThemeDialogProps> = ({ open, initial, onClose, onSave }) => {
  const [dashBg, setDashBg] = useState('');
  const [widgetBg, setWidgetBg] = useState('');
  const [widgetBorder, setWidgetBorder] = useState('');
  const [widgetRadius, setWidgetRadius] = useState(4);
  const [widgetOpacity, setWidgetOpacity] = useState(1);
  const [titleColor, setTitleColor] = useState('');
  const [contentColor, setContentColor] = useState('');
  const [hideHeaders, setHideHeaders] = useState(false);
  const [hideLabels, setHideLabels] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = initial ?? {};
    setDashBg(t.dashboardBackground ?? '');
    setWidgetBg(t.widgetBackground ?? '');
    setWidgetBorder(t.widgetBorderColor ?? '');
    setWidgetRadius(t.widgetBorderRadius ?? 4);
    setWidgetOpacity(t.widgetOpacity ?? 1);
    setTitleColor(t.titleColor ?? '');
    setContentColor(t.contentTextColor ?? '');
    setHideHeaders(t.hideHeaders ?? false);
    setHideLabels(t.hideLabels ?? false);
  }, [open, initial]);

  const handleReset = () => {
    setDashBg('');
    setWidgetBg('');
    setWidgetBorder('');
    setWidgetRadius(4);
    setWidgetOpacity(1);
    setTitleColor('');
    setContentColor('');
    setHideHeaders(false);
    setHideLabels(false);
  };

  const handleSave = () => {
    const theme: DashboardTheme = {};
    if (dashBg.trim()) theme.dashboardBackground = dashBg.trim();
    if (widgetBg.trim()) theme.widgetBackground = widgetBg.trim();
    if (widgetBorder.trim()) theme.widgetBorderColor = widgetBorder.trim();
    if (widgetRadius !== 4) theme.widgetBorderRadius = widgetRadius;
    if (widgetOpacity !== 1) theme.widgetOpacity = widgetOpacity;
    if (titleColor.trim()) theme.titleColor = titleColor.trim();
    if (contentColor.trim()) theme.contentTextColor = contentColor.trim();
    if (hideHeaders) theme.hideHeaders = true;
    if (hideLabels) theme.hideLabels = true;
    onSave(Object.keys(theme).length > 0 ? theme : undefined);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Dashboard Theme</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="overline" color="text.secondary">Layout</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Stack spacing={2}>
              <SettingsColorPicker
                label="Dashboard background"
                value={dashBg}
                onChange={setDashBg}
                placeholder="#0d1117 or rgba(...)"
              />
              <FormControlLabel
                control={<Switch checked={hideHeaders} onChange={(e) => setHideHeaders(e.target.checked)} />}
                label="Hide widget headers"
              />
              <FormControlLabel
                control={<Switch checked={hideLabels} onChange={(e) => setHideLabels(e.target.checked)} />}
                label="Hide data-source labels"
              />
            </Stack>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">Widget defaults</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Stack spacing={2}>
              <SettingsColorPicker
                label="Widget background"
                value={widgetBg}
                onChange={setWidgetBg}
                placeholder="#1e1e1e or rgba(...)"
              />
              <SettingsColorPicker
                label="Widget border color"
                value={widgetBorder}
                onChange={setWidgetBorder}
                placeholder="#444 or rgba(...)"
              />
              <SettingsSlider
                label="Corner radius"
                value={widgetRadius}
                onChange={setWidgetRadius}
                min={0}
                max={24}
                unit="px"
              />
              <SettingsOpacitySlider
                label="Widget opacity"
                value={widgetOpacity}
                onChange={setWidgetOpacity}
              />
              <SettingsColorPicker
                label="Title color"
                value={titleColor}
                onChange={setTitleColor}
                placeholder="#ffffff or rgba(...)"
              />
              <SettingsColorPicker
                label="Content text color"
                value={contentColor}
                onChange={setContentColor}
                placeholder="rgba(...) or hex"
              />
            </Stack>
          </Box>

          <Button variant="outlined" size="small" color="warning" onClick={handleReset} sx={{ alignSelf: 'flex-start' }}>
            Reset
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardThemeDialog;
